<template>
    <div class="seller-stats-page">
        <!-- Back button and header -->
        <div class="page-header">
            <button class="back-btn" @click="$router.back()">
                <i class="material-icons">arrow_back</i>
                <span>Retour</span>
            </button>
        </div>

        <div v-if="loading" class="loading-state">
            <div class="spinner"></div>
            <span>Chargement des statistiques...</span>
        </div>

        <template v-else-if="seller">
            <!-- Seller Profile Card -->
            <div class="profile-card">
                <div class="profile-header">
                    <div class="avatar">
                        <img v-if="seller.avatar" :src="seller.avatar" :alt="seller.fullName" />
                        <span v-else>{{ getInitial(seller) }}</span>
                    </div>
                    <div class="profile-info">
                        <h1>{{ seller.fullName || 'Vendeur' }}</h1>
                        <p class="email">{{ seller.email }}</p>
                        <div class="badges">
                            <span class="badge country">
                                <i class="material-icons">location_on</i>
                                {{ formatCountries(seller.countries) || 'N/A' }}
                            </span>
                            <span class="badge status" :class="{ online: isOnline }">
                                <i class="material-icons">circle</i>
                                {{ isOnline ? 'En ligne' : 'Hors ligne' }}
                            </span>
                        </div>
                    </div>
                    <div class="profile-actions">
                        <button class="action-btn primary" @click="startChat">
                            <i class="material-icons">chat</i>
                            <span>Envoyer un message</span>
                        </button>
                    </div>
                </div>
            </div>

            <!-- Date Filter -->
            <div class="date-filter-card">
                <div class="filter-header">
                    <h3>Filtrer par période</h3>
                </div>
                <div class="filter-content">
                    <div class="filter-group">
                        <label>Période</label>
                        <select v-model="dateType" @change="onDateTypeChange" class="date-select">
                            <option value="today">Aujourd'hui</option>
                            <option value="yesterday">Hier</option>
                            <option value="thisweek">Cette semaine</option>
                            <option value="thismonth">Ce mois</option>
                            <option value="last30days">30 derniers jours</option>
                            <option value="custom">Personnalisé</option>
                        </select>
                    </div>
                    <div v-if="dateType === 'custom'" class="custom-dates">
                        <div class="filter-group">
                            <label>Du</label>
                            <input type="date" v-model="dateFrom" class="date-input" />
                        </div>
                        <div class="filter-group">
                            <label>Au</label>
                            <input type="date" v-model="dateTo" class="date-input" />
                        </div>
                    </div>
                    <button @click="applyDateFilter" class="apply-btn" :disabled="loadingStats">
                        <i class="material-icons">filter_list</i>
                        Filtrer
                    </button>
                </div>
            </div>

            <!-- Stats Overview -->
            <div class="stats-section">
                <div class="section-header-with-badge">
                    <h2>Aperçu des performances</h2>
                    <span class="date-badge" v-if="currentDateLabel">{{ currentDateLabel }}</span>
                </div>
                <div class="stats-grid">
                    <div class="stat-card">
                        <div class="stat-icon orders">
                            <i class="material-icons">shopping_cart</i>
                        </div>
                        <div class="stat-content">
                            <span class="stat-value">{{ stats.totalOrders }}</span>
                            <span class="stat-label">Commandes totales</span>
                        </div>
                    </div>

                    <div class="stat-card">
                        <div class="stat-icon delivered">
                            <i class="material-icons">check_circle</i>
                        </div>
                        <div class="stat-content">
                            <span class="stat-value">{{ stats.deliveredOrders }}</span>
                            <span class="stat-label">Commandes livrées</span>
                        </div>
                    </div>

                    <div class="stat-card">
                        <div class="stat-icon pending">
                            <i class="material-icons">schedule</i>
                        </div>
                        <div class="stat-content">
                            <span class="stat-value">{{ stats.pendingOrders }}</span>
                            <span class="stat-label">En attente</span>
                        </div>
                    </div>

                    <div class="stat-card">
                        <div class="stat-icon cancelled">
                            <i class="material-icons">block</i>
                        </div>
                        <div class="stat-content">
                            <span class="stat-value">{{ stats.cancelledOrders }}</span>
                            <span class="stat-label">Annulées</span>
                        </div>
                    </div>

                    <div class="stat-card">
                        <div class="stat-icon revenue">
                            <i class="material-icons">attach_money</i>
                        </div>
                        <div class="stat-content">
                            <span class="stat-value">{{ formatCurrency(stats.totalRevenue) }}</span>
                            <span class="stat-label">Revenus totaux</span>
                        </div>
                    </div>

                    <div class="stat-card">
                        <div class="stat-icon products">
                            <i class="material-icons">category</i>
                        </div>
                        <div class="stat-content">
                            <span class="stat-value">{{ stats.totalProducts }}</span>
                            <span class="stat-label">Produits actifs</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Recent Orders -->
            <div class="orders-section">
                <div class="section-header">
                    <div class="section-title-group">
                        <h2>Commandes récentes</h2>
                        <span class="date-badge" v-if="currentDateLabel">{{ currentDateLabel }}</span>
                    </div>
                    <span class="read-only-badge">
                        <i class="material-icons">visibility</i>
                        Lecture seule
                    </span>
                </div>

                <div v-if="loadingStats" class="loading-orders">
                    <div class="spinner small"></div>
                    <span>Chargement...</span>
                </div>

                <div v-else-if="recentOrders.length === 0" class="empty-orders">
                    <i class="material-icons">receipt_long</i>
                    <p>Aucune commande pour cette période</p>
                </div>

                <table v-else class="orders-table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Client</th>
                            <th>Montant</th>
                            <th>Statut</th>
                            <th>Date</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="order in recentOrders" :key="order._id">
                            <td>
                                <span class="order-id">#{{ order.id }}</span>
                            </td>
                            <td>
                                <div class="customer-info">
                                    <span class="name">{{ (order.customer && order.customer.fullName) || 'N/A' }}</span>
                                    <span class="phone">{{ (order.customer && order.customer.phone) || '' }}</span>
                                </div>
                            </td>
                            <td>
                                <span class="amount">{{ formatCurrency(order.totalPrice) }}</span>
                            </td>
                            <td>
                                <span
                                    class="status-badge"
                                    :style="{ backgroundColor: getStatusBgColor(order), color: order.statusColor || '#6b7280' }"
                                >
                                    {{ order.statusName || 'N/A' }}
                                </span>
                            </td>
                            <td>
                                <span class="date">{{ formatDate(order.createdAt) }}</span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Top Products -->
            <div class="products-section">
                <h2>Produits populaires</h2>

                <div v-if="topProducts.length === 0" class="empty-products">
                    <i class="material-icons">inventory</i>
                    <p>Aucun produit trouvé</p>
                </div>

                <div v-else class="products-grid">
                    <div v-for="product in topProducts" :key="product._id" class="product-card">
                        <div class="product-image">
                            <img
                                v-if="product.image"
                                :src="product.image"
                                :alt="product.name"
                            />
                            <i v-else class="material-icons">image</i>
                        </div>
                        <div class="product-info">
                            <h4>{{ product.name }}</h4>
                            <p class="price">{{ formatCurrency(product.price) }}</p>
                            <p class="orders-count">{{ product.ordersCount || 0 }} commandes</p>
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <div v-else class="error-state">
            <i class="material-icons">error_outline</i>
            <h3>Vendeur introuvable</h3>
            <p>Ce vendeur n'existe pas ou n'est pas assigné à votre compte</p>
            <button class="back-link" @click="$router.back()">
                Retour à la liste
            </button>
        </div>
    </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';

export default {
    name: 'SellerStats',
    data() {
        return {
            seller: null,
            stats: {
                totalOrders: 0,
                deliveredOrders: 0,
                pendingOrders: 0,
                cancelledOrders: 0,
                totalRevenue: 0,
                totalProducts: 0
            },
            recentOrders: [],
            topProducts: [],
            loading: true,
            loadingStats: false,
            // Date filter
            dateType: 'last30days',
            dateFrom: '',
            dateTo: ''
        };
    },
    computed: {
        ...mapState('chat', ['onlineUsers']),
        sellerId() {
            return this.$route.params.id;
        },
        isOnline() {
            if (!this.seller || !this.onlineUsers) return false;
            if (this.onlineUsers instanceof Set) {
                return this.onlineUsers.has(this.seller._id);
            }
            if (Array.isArray(this.onlineUsers)) {
                return this.onlineUsers.includes(this.seller._id);
            }
            // onlineUsers is an object { oduserId: boolean }
            return this.onlineUsers[this.seller._id] === true;
        },
        currentDateLabel() {
            const labels = {
                'today': "Aujourd'hui",
                'yesterday': 'Hier',
                'thisweek': 'Cette semaine',
                'thismonth': 'Ce mois',
                'last30days': '30 derniers jours',
                'custom': this.dateFrom && this.dateTo ? `${this.formatDateShort(this.dateFrom)} - ${this.formatDateShort(this.dateTo)}` : ''
            };
            return labels[this.dateType] || '';
        }
    },
    async mounted() {
        this.initDefaultDates();
        // Initialize socket connection for online status tracking
        this.initSocket();
        await this.loadSellerStats();
    },
    methods: {
        ...mapActions('chat', ['getOnlineStatus', 'initSocket']),
        initDefaultDates() {
            const today = new Date();
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(today.getDate() - 30);
            this.dateTo = today.toISOString().split('T')[0];
            this.dateFrom = thirtyDaysAgo.toISOString().split('T')[0];
        },
        onDateTypeChange() {
            if (this.dateType !== 'custom') {
                this.applyDateFilter();
            }
        },
        async applyDateFilter() {
            this.loadingStats = true;
            await this.loadSellerStats(false);
            this.loadingStats = false;
        },
        async loadSellerStats(showMainLoader = true) {
            if (showMainLoader) this.loading = true;
            try {
                const params = {
                    sellerId: this.sellerId
                };

                // Add date filter parameters
                if (this.dateType === 'custom' && this.dateFrom && this.dateTo) {
                    params.dateFrom = this.dateFrom;
                    params.dateTo = this.dateTo;
                } else {
                    params.dateType = this.dateType;
                }

                const response = await this.$server.getSellerStats('accountManager', params);

                if (response && response.content) {
                    this.seller = response.content.seller;
                    // Map the stats from backend format
                    const statsData = response.content.stats || {};
                    this.stats = {
                        totalOrders: (statsData.orders && statsData.orders.total) || 0,
                        deliveredOrders: (statsData.shippings && statsData.shippings.delivered) || 0,
                        pendingOrders: (statsData.shippings && statsData.shippings.pending) || 0,
                        cancelledOrders: (statsData.shippings && statsData.shippings.cancelled) || 0,
                        totalRevenue: (statsData.orders && statsData.orders.revenue) || 0,
                        totalProducts: statsData.products || 0
                    };
                    this.recentOrders = response.content.recentOrders || [];
                    this.topProducts = response.content.topProducts || [];

                    // Fetch online status for this seller
                    if (this.seller && this.seller._id) {
                        this.getOnlineStatus([this.seller._id]);
                    }
                }
            } catch (error) {
                console.error('Failed to load seller stats:', error);
                if (this.$toast) this.$toast.error('Erreur lors du chargement des statistiques');
            } finally {
                if (showMainLoader) this.loading = false;
            }
        },
        getInitial(seller) {
            if (!seller) return '';
            const name = seller.fullName || seller.firstName || seller.email || '';
            return name.charAt(0).toUpperCase();
        },
        formatCurrency(amount) {
            if (!amount) return '0 MAD';
            return new Intl.NumberFormat('fr-FR', {
                style: 'decimal',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
            }).format(amount) + ' MAD';
        },
        formatDate(dateStr) {
            if (!dateStr) return '-';
            return new Date(dateStr).toLocaleDateString('fr-FR', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
            });
        },
        formatDateShort(dateStr) {
            if (!dateStr) return '';
            const date = new Date(dateStr);
            return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
        },
        formatCountries(countries) {
            if (!countries || countries.length === 0) return null;
            return countries.join(', ');
        },
        getStatusBgColor(order) {
            if (order.statusColor) {
                // Convert hex color to rgba with low opacity for background
                return order.statusColor + '20';
            }
            return '#f3f4f6';
        },
        getStatusClass(status) {
            const statusMap = {
                'delivered': 'success',
                'livré': 'success',
                'pending': 'warning',
                'en attente': 'warning',
                'cancelled': 'danger',
                'annulé': 'danger',
                'shipped': 'info',
                'expédié': 'info'
            };
            return statusMap[status && status.toLowerCase()] || 'default';
        },
        async startChat() {
            try {
                const response = await this.$server.getOrCreateConversation('conversations', {
                    sellerId: this.sellerId
                });

                if (response && response.content) {
                    this.$router.push(`/account-manager/chat/${response.content._id}`);
                }
            } catch (error) {
                console.error('Failed to start chat:', error);
                if (this.$toast) this.$toast.error('Erreur lors de l\'ouverture du chat');
            }
        }
    }
};
</script>

<style scoped>
.seller-stats-page {
    padding: 24px;
}

.page-header {
    margin-bottom: 24px;
}

.back-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    border: none;
    background: none;
    cursor: pointer;
    color: #6b7280;
    font-size: 14px;
    transition: color 0.2s;
}

.back-btn:hover {
    color: #111827;
}

.loading-state,
.error-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    color: #9ca3af;
}

.spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #e5e7eb;
    border-top-color: #059669;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin-bottom: 16px;
}

.spinner.small {
    width: 24px;
    height: 24px;
    border-width: 2px;
    margin-bottom: 8px;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

.error-state i {
    font-size: 64px;
    margin-bottom: 16px;
    color: #ef4444;
}

.error-state h3 {
    margin: 0 0 8px;
    font-size: 18px;
    color: #111827;
}

.back-link {
    margin-top: 16px;
    padding: 10px 20px;
    background-color: #059669;
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
}

.profile-card {
    background: #ffffff;
    border-radius: 16px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    padding: 24px;
    margin-bottom: 24px;
}

.profile-header {
    display: flex;
    align-items: center;
    gap: 24px;
}

.avatar {
    width: 96px;
    height: 96px;
    border-radius: 50%;
    overflow: hidden;
    background: linear-gradient(135deg, #059669 0%, #10b981 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 36px;
    font-weight: 600;
    flex-shrink: 0;
}

.avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.profile-info {
    flex: 1;
}

.profile-info h1 {
    margin: 0;
    font-size: 24px;
    font-weight: 700;
    color: #111827;
}

.profile-info .email {
    margin: 4px 0 12px;
    color: #6b7280;
    font-size: 14px;
}

.badges {
    display: flex;
    gap: 12px;
}

.badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 13px;
    font-weight: 500;
}

.badge i {
    font-size: 16px;
}

.badge.country {
    background-color: #ecfdf5;
    color: #059669;
}

.badge.status {
    background-color: #f3f4f6;
    color: #6b7280;
}

.badge.status.online {
    background-color: #ecfdf5;
    color: #10b981;
}

.badge.status i {
    font-size: 10px;
}

.profile-actions {
    display: flex;
    gap: 12px;
}

.action-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 20px;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
}

.action-btn.primary {
    background: linear-gradient(135deg, #059669 0%, #10b981 100%);
    color: white;
}

.action-btn.primary:hover {
    box-shadow: 0 4px 12px rgba(5, 150, 105, 0.3);
}

/* Date Filter Card */
.date-filter-card {
    background: #ffffff;
    border-radius: 16px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    padding: 20px 24px;
    margin-bottom: 24px;
}

.filter-header h3 {
    margin: 0 0 16px;
    font-size: 16px;
    font-weight: 600;
    color: #111827;
}

.filter-content {
    display: flex;
    align-items: flex-end;
    gap: 16px;
    flex-wrap: wrap;
}

.filter-group {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.filter-group label {
    font-size: 12px;
    font-weight: 500;
    color: #6b7280;
}

.date-select,
.date-input {
    padding: 8px 12px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 14px;
    color: #111827;
    background: #fff;
    min-width: 150px;
}

.date-select:focus,
.date-input:focus {
    outline: none;
    border-color: #059669;
    box-shadow: 0 0 0 2px rgba(5, 150, 105, 0.2);
}

.custom-dates {
    display: flex;
    gap: 12px;
}

.apply-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    background: #059669;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s;
}

.apply-btn:hover:not(:disabled) {
    background: #047857;
}

.apply-btn:disabled {
    background: #9ca3af;
    cursor: not-allowed;
}

.apply-btn i {
    font-size: 18px;
}

.stats-section,
.orders-section,
.products-section {
    background: #ffffff;
    border-radius: 16px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    padding: 24px;
    margin-bottom: 24px;
}

.stats-section h2,
.orders-section h2,
.products-section h2 {
    margin: 0 0 20px;
    font-size: 18px;
    font-weight: 600;
    color: #111827;
}

.section-header-with-badge {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 20px;
}

.section-header-with-badge h2 {
    margin: 0;
}

.date-badge {
    font-size: 12px;
    padding: 4px 10px;
    background: #ecfdf5;
    color: #059669;
    border-radius: 4px;
    font-weight: 500;
}

.section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
}

.section-title-group {
    display: flex;
    align-items: center;
    gap: 12px;
}

.section-title-group h2 {
    margin: 0;
}

.read-only-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 6px 12px;
    background-color: #fef3c7;
    color: #d97706;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 500;
}

.read-only-badge i {
    font-size: 14px;
}

.stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 16px;
}

.stat-card {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px;
    background-color: #f9fafb;
    border-radius: 12px;
}

.stat-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.stat-icon i {
    font-size: 24px;
    color: #ffffff;
}

.stat-icon.orders { background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%); }
.stat-icon.delivered { background: linear-gradient(135deg, #10b981 0%, #059669 100%); }
.stat-icon.pending { background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); }
.stat-icon.cancelled { background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%); }
.stat-icon.revenue { background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%); }
.stat-icon.products { background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%); }

.stat-content {
    display: flex;
    flex-direction: column;
}

.stat-value {
    font-size: 20px;
    font-weight: 700;
    color: #111827;
}

.stat-label {
    font-size: 12px;
    color: #6b7280;
}

.loading-orders {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 40px;
    color: #6b7280;
}

.empty-orders,
.empty-products {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 40px;
    color: #9ca3af;
}

.empty-orders i,
.empty-products i {
    font-size: 48px;
    margin-bottom: 12px;
    opacity: 0.5;
}

.orders-table {
    width: 100%;
    border-collapse: collapse;
}

.orders-table th,
.orders-table td {
    padding: 12px 16px;
    text-align: left;
    border-bottom: 1px solid #e5e7eb;
}

.orders-table th {
    font-size: 12px;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
}

.order-id {
    font-weight: 600;
    color: #059669;
}

.customer-info {
    display: flex;
    flex-direction: column;
}

.customer-info .name {
    font-weight: 500;
    color: #111827;
}

.customer-info .phone {
    font-size: 12px;
    color: #6b7280;
}

.amount {
    font-weight: 600;
    color: #111827;
}

.status-badge {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
}

.status-badge.success { background-color: #ecfdf5; color: #059669; }
.status-badge.warning { background-color: #fef3c7; color: #d97706; }
.status-badge.danger { background-color: #fef2f2; color: #dc2626; }
.status-badge.info { background-color: #eff6ff; color: #3b82f6; }
.status-badge.default { background-color: #f3f4f6; color: #6b7280; }

.date {
    font-size: 13px;
    color: #6b7280;
}

.products-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 16px;
}

.product-card {
    display: flex;
    gap: 12px;
    padding: 12px;
    background-color: #f9fafb;
    border-radius: 12px;
}

.product-image {
    width: 64px;
    height: 64px;
    border-radius: 8px;
    overflow: hidden;
    background-color: #e5e7eb;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.product-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.product-image i {
    font-size: 24px;
    color: #9ca3af;
}

.product-info h4 {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: #111827;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.product-info .price {
    margin: 4px 0;
    font-size: 13px;
    color: #059669;
    font-weight: 600;
}

.product-info .orders-count {
    margin: 0;
    font-size: 12px;
    color: #6b7280;
}

@media (max-width: 768px) {
    .seller-stats-page {
        padding: 16px;
    }

    .profile-header {
        flex-direction: column;
        text-align: center;
    }

    .badges {
        justify-content: center;
    }

    .profile-actions {
        width: 100%;
        justify-content: center;
    }

    .filter-content {
        flex-direction: column;
        align-items: stretch;
    }

    .custom-dates {
        flex-direction: column;
    }

    .apply-btn {
        width: 100%;
        justify-content: center;
    }

    .stats-grid {
        grid-template-columns: repeat(2, 1fr);
    }

    .orders-table {
        font-size: 13px;
    }

    .orders-table th,
    .orders-table td {
        padding: 8px;
    }

    .section-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
    }
}
</style>
